/**
 * Design System - Standardized design tokens for NewValuate
 *
 * This file contains all design tokens to ensure visual consistency
 * across the application.
 */

// ============================================================================
// SPACING SYSTEM - 8px base unit
// ============================================================================
export const spacing = {
  xs: 4,    // 4px
  sm: 8,    // 8px
  md: 16,   // 16px
  lg: 24,   // 24px
  xl: 32,   // 32px
  '2xl': 48, // 48px
  '3xl': 64, // 64px
} as const;

// ============================================================================
// BORDER RADIUS SYSTEM
// ============================================================================
export const borderRadius = {
  none: 0,
  xs: 8,    // Small elements (chips, badges)
  sm: 12,   // Buttons, inputs
  md: 16,   // Cards, modals
  lg: 24,   // Large containers, hero sections
  full: 9999, // Pills, circular elements
} as const;

// ============================================================================
// SHADOW SYSTEM - 4 elevation levels
// ============================================================================
export const shadows = {
  // Level 0: No shadow
  none: 'none',

  // Level 1: Subtle lift (cards at rest)
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',

  // Level 2: Standard elevation (cards on hover, dropdowns)
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',

  // Level 3: High elevation (modals, popovers)
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',

  // Level 4: Maximum elevation (notifications, tooltips)
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',

  // Colored shadows for primary actions
  primarySm: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
  primaryMd: '0 10px 15px -3px rgba(59, 130, 246, 0.4)',
} as const;

// ============================================================================
// TYPOGRAPHY SYSTEM
// ============================================================================
export const typography = {
  fontFamily: {
    base: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    mono: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },

  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem',// 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },

  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const;

// ============================================================================
// COLOR PALETTE - Semantic colors for both themes
// ============================================================================
export const colors = {
  // Primary brand colors
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',  // Main primary
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },

  // Secondary/accent colors
  secondary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',  // Main secondary
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  // Success (green)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',  // Main success
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  // Warning (yellow/orange)
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',  // Main warning
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  // Error (red)
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',  // Main error
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // Info (blue)
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',  // Main info
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },

  // Neutral grays
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
} as const;

// ============================================================================
// TRANSITIONS & ANIMATIONS
// ============================================================================
export const transitions = {
  duration: {
    fastest: 100,
    fast: 150,
    normal: 200,
    slow: 300,
    slowest: 500,
  },

  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
} as const;

// ============================================================================
// BREAKPOINTS - Mobile-first responsive design
// ============================================================================
export const breakpoints = {
  xs: 0,      // Mobile
  sm: 640,    // Tablet
  md: 768,    // Small desktop
  lg: 1024,   // Desktop
  xl: 1280,   // Large desktop
  '2xl': 1536, // Extra large
} as const;

// ============================================================================
// Z-INDEX SYSTEM - Layering hierarchy
// ============================================================================
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const;

// ============================================================================
// COMPONENT SPECIFIC TOKENS
// ============================================================================

// Button variants
export const button = {
  sizes: {
    sm: {
      height: 32,
      padding: `${spacing.xs}px ${spacing.md}px`,
      fontSize: typography.fontSize.sm,
    },
    md: {
      height: 40,
      padding: `${spacing.sm}px ${spacing.lg}px`,
      fontSize: typography.fontSize.base,
    },
    lg: {
      height: 48,
      padding: `${spacing.md}px ${spacing.xl}px`,
      fontSize: typography.fontSize.lg,
    },
  },
} as const;

// Card variants
export const card = {
  padding: {
    sm: 12,            // 12px (compact)
    md: spacing.md,    // 16px
    lg: spacing.lg,    // 24px
  },
  borderRadius: borderRadius.md,
  elevation: {
    resting: shadows.sm,
    hover: shadows.md,
    active: shadows.lg,
  },
} as const;

// Input fields
export const input = {
  height: {
    sm: 36,
    md: 44,
    lg: 52,
  },
  borderRadius: borderRadius.sm,
  borderWidth: 1,
} as const;

// ============================================================================
// CHAT COMPONENT TOKENS
// ============================================================================
export const chatTokens = {
  message: {
    maxWidth: '70%',
    padding: { x: 20, y: 16 },
    borderRadius: {
      user: '20px 20px 4px 20px',
      assistant: '4px 20px 20px 20px',
    },
    gap: 24,
  },
  sidebar: {
    width: 280,
    itemPadding: { x: 16, y: 12 },
    selectedAccent: 4,
  },
  input: {
    minHeight: 52,
    maxHeight: 200,
    borderRadius: 26,
  },
  insight: {
    accentWidth: 4,
    padding: 20,
    metricColumns: 3,
  },
  propertyBar: {
    height: 48,
  },
} as const;

// Insight card type colors
export const insightColors = {
  valuation: colors.success[500],   // Green - property value
  market: colors.info[500],         // Blue - market analysis
  financial: colors.primary[500],   // Primary blue - financial metrics
  risk: colors.warning[500],        // Amber - risk assessment
  general: colors.gray[500],        // Gray - general queries
} as const;

export type InsightType = keyof typeof insightColors;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create consistent alpha (opacity) values
 */
export const alpha = (color: string, opacity: number): string => {
  // Simple alpha helper - in production, use a proper color library
  return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

/**
 * Get responsive value based on breakpoint
 */
export const responsive = <T,>(values: {
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
}) => values;

/**
 * Create gradient backgrounds
 */
export const gradients = {
  primary: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[700]})`,
  secondary: `linear-gradient(135deg, ${colors.secondary[500]}, ${colors.secondary[700]})`,
  success: `linear-gradient(135deg, ${colors.success[500]}, ${colors.success[700]})`,
  branded: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
  subtle: `linear-gradient(135deg, ${colors.gray[50]}, ${colors.gray[100]})`,
} as const;

// ============================================================================
// EXPORT ALL
// ============================================================================
export const designSystem = {
  spacing,
  borderRadius,
  shadows,
  typography,
  colors,
  transitions,
  breakpoints,
  zIndex,
  button,
  card,
  input,
  chatTokens,
  insightColors,
  alpha,
  responsive,
  gradients,
} as const;

export default designSystem;
